import {
  BACKDROP_RADIUS,
  HOME_CAMERA_Z,
  HOME_FOV,
  backdropExtent,
  hexToRgb01,
  palette,
  universeData,
  universeDefaults,
} from "@ptp/look";
import universeVertexShader from "@ptp/look/shaders/universe.vert.glsl?raw";
import universeFragmentShader from "@ptp/look/shaders/universe.frag.glsl?raw";
import backdropVertexShader from "@ptp/look/shaders/backdrop.vert.glsl?raw";
import backdropFragmentShader from "@ptp/look/shaders/backdrop.frag.glsl?raw";
import { universeTuning } from "./universeTuning.js";

/**
 * Sparser than the projection's 40k: a portrait phone sees about a third of the field's
 * width, so this keeps a similar density on screen at a fraction of the cost.
 */
const DUST_COUNT = 8_000;
const HAZE_COUNT = 150;

/** The projection's resting camera; it never moves here. */
const NEAR = 0.1;
const FAR = 100;

/** The built-ins three.js prepends to the shared shaders. */
const VERTEX_PRELUDE = `precision highp float;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;
attribute vec3 position;
attribute vec2 uv;
`;
const FRAGMENT_PRELUDE = "precision highp float;\n";

type Mat4 = Float32Array;

/**
 * The projection's background universe (apps/visuals BackgroundUniverse) in raw WebGL:
 * the same shaders, colours and dust from @ptp/look, seen from a fixed camera. No three.js,
 * so it costs the phone one quad and one points draw per frame.
 */
export class UniverseRenderer {
  readonly #gl: WebGLRenderingContext;
  readonly #backdrop: Program;
  readonly #dust: Program;
  readonly #quad: WebGLBuffer;
  readonly #data: WebGLBuffer;
  readonly #style: WebGLBuffer;
  readonly #modelView: Mat4;
  readonly #view: Mat4 = translation(0, 0, -HOME_CAMERA_Z);
  #projection: Mat4 = perspective(HOME_FOV, 1);
  #extent = backdropExtent(1);
  #width = 1;
  #height = 1;
  #time = 0;
  #spinTime = 0;
  #flowTime = 0;

  /** Null when the device has no WebGL; the page background stands in then. */
  static create(canvas: HTMLCanvasElement): UniverseRenderer | null {
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      powerPreference: "low-power",
    });
    if (!gl) return null;
    try {
      return new UniverseRenderer(gl);
    } catch (err) {
      console.warn("[join] background disabled", err);
      return null;
    }
  }

  private constructor(gl: WebGLRenderingContext) {
    this.#gl = gl;
    this.#backdrop = program(gl, backdropVertexShader, backdropFragmentShader);
    this.#dust = program(gl, universeVertexShader, universeFragmentShader);

    // Two triangles over clip space; uv derived from the same corners.
    this.#quad = buffer(gl, new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]));
    const { data, style } = universeData(DUST_COUNT, HAZE_COUNT);
    this.#data = buffer(gl, data);
    this.#style = buffer(gl, style);

    this.#modelView = multiply(
      translation(0, 0, -HOME_CAMERA_Z),
      multiply(rotationX(universeDefaults.tiltX), rotationY(universeDefaults.tiltY)),
    );
  }

  /** Size in CSS pixels; `dpr` scales the drawing buffer. */
  resize(width: number, height: number, dpr: number): void {
    const canvas = this.#gl.canvas as HTMLCanvasElement;
    this.#width = Math.max(1, Math.round(width * dpr));
    this.#height = Math.max(1, Math.round(height * dpr));
    canvas.width = this.#width;
    canvas.height = this.#height;
    this.#projection = perspective(HOME_FOV, this.#width / this.#height);
    this.#extent = backdropExtent(this.#width / this.#height);
  }

  /** Advances the clocks by `dt` seconds and draws a frame. */
  render(dt: number): void {
    this.#time += dt;
    this.#spinTime += dt * universeTuning.speed;
    this.#flowTime += dt * universeTuning.flow;

    const gl = this.#gl;
    gl.viewport(0, 0, this.#width, this.#height);

    gl.disable(gl.BLEND);
    this.#use(this.#backdrop);
    this.#bindQuad(this.#backdrop);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Premultiplied like the projection; the dust only writes alpha 0, so it adds.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.#use(this.#dust);
    this.#bindDust(this.#dust);
    gl.drawArrays(gl.POINTS, 0, DUST_COUNT + HAZE_COUNT);
  }

  dispose(): void {
    const gl = this.#gl;
    for (const b of [this.#quad, this.#data, this.#style]) gl.deleteBuffer(b);
    for (const p of [this.#backdrop, this.#dust]) gl.deleteProgram(p.program);
  }

  #use(p: Program): void {
    const gl = this.#gl;
    gl.useProgram(p.program);
    const f = (name: string, value: number): void => {
      const at = p.uniform(name);
      if (at) gl.uniform1f(at, value);
    };
    const color = (name: string, hex: string): void => {
      const at = p.uniform(name);
      if (at) gl.uniform3fv(at, hexToRgb01(hex));
    };
    const mat = (name: string, value: Mat4): void => {
      const at = p.uniform(name);
      if (at) gl.uniformMatrix4fv(at, false, value);
    };

    f("uTime", this.#time);
    f("uSpinTime", this.#spinTime);
    f("uFlowTime", this.#flowTime);
    const resolution = p.uniform("uResolution");
    if (resolution) gl.uniform2f(resolution, this.#width, this.#height);
    f("uSize", universeTuning.size);
    f("uHaze", universeTuning.haze);
    f("uTwinkle", universeTuning.twinkle);
    f("uGlint", universeTuning.glint);
    f("uFocus", universeDefaults.focus);
    f("uAperture", universeDefaults.aperture);
    f("uOpacity", universeTuning.opacity);
    f("uDim", 0);
    f("uInner", universeDefaults.inner);
    f("uBackdrop", 1);
    f("uSkyRadius", BACKDROP_RADIUS);
    const extent = p.uniform("uExtent");
    if (extent) gl.uniform2f(extent, this.#extent[0], this.#extent[1]);
    color("uCool", palette.universe.cool);
    color("uWarm", palette.universe.warm);
    color("uDeep", palette.universe.deep);
    color("uLight", palette.universe.light);
    mat("modelViewMatrix", this.#modelView);
    mat("projectionMatrix", this.#projection);
    mat("viewMatrix", this.#view);
  }

  #bindQuad(p: Program): void {
    const gl = this.#gl;
    this.#disableAttributes();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.#quad);
    attribute(gl, p.attribute("position"), 2, 16, 0);
    attribute(gl, p.attribute("uv"), 2, 16, 8);
  }

  #bindDust(p: Program): void {
    const gl = this.#gl;
    this.#disableAttributes();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.#data);
    attribute(gl, p.attribute("aData"), 4, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.#style);
    attribute(gl, p.attribute("aStyle"), 2, 0, 0);
  }

  #disableAttributes(): void {
    const gl = this.#gl;
    const count = gl.getParameter(gl.MAX_VERTEX_ATTRIBS) as number;
    for (let i = 0; i < count; i++) gl.disableVertexAttribArray(i);
  }
}

interface Program {
  program: WebGLProgram;
  uniform(name: string): WebGLUniformLocation | null;
  attribute(name: string): number;
}

function program(gl: WebGLRenderingContext, vertex: string, fragment: string): Program {
  const compile = (type: number, source: string): WebGLShader => {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("createShader failed");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(shader) ?? "shader compile failed");
    }
    return shader;
  };
  const p = gl.createProgram();
  if (!p) throw new Error("createProgram failed");
  gl.attachShader(p, compile(gl.VERTEX_SHADER, VERTEX_PRELUDE + vertex));
  gl.attachShader(p, compile(gl.FRAGMENT_SHADER, FRAGMENT_PRELUDE + fragment));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(p) ?? "program link failed");
  }
  const uniforms = new Map<string, WebGLUniformLocation | null>();
  return {
    program: p,
    uniform: (name) => {
      if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(p, name));
      return uniforms.get(name) ?? null;
    },
    attribute: (name) => gl.getAttribLocation(p, name),
  };
}

function buffer(gl: WebGLRenderingContext, data: Float32Array): WebGLBuffer {
  const b = gl.createBuffer();
  if (!b) throw new Error("createBuffer failed");
  gl.bindBuffer(gl.ARRAY_BUFFER, b);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  return b;
}

/** Points the bound buffer at `location`; -1 means the shader optimised it away. */
function attribute(
  gl: WebGLRenderingContext,
  location: number,
  size: number,
  stride: number,
  offset: number,
): void {
  if (location < 0) return;
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset);
}

// Column-major 4x4 matrices, as WebGL expects them.

function perspective(fovDeg: number, aspect: number): Mat4 {
  const f = 1 / Math.tan((fovDeg * Math.PI) / 360);
  const nf = 1 / (NEAR - FAR);
  // prettier-ignore
  return new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (FAR + NEAR) * nf, -1,
    0, 0, 2 * FAR * NEAR * nf, 0,
  ]);
}

function translation(x: number, y: number, z: number): Mat4 {
  // prettier-ignore
  return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]);
}

function rotationX(a: number): Mat4 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  // prettier-ignore
  return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
}

function rotationY(a: number): Mat4 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  // prettier-ignore
  return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]);
}

function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row]! * b[col * 4 + k]!;
      out[col * 4 + row] = sum;
    }
  }
  return out;
}
